import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { object, ObjectSchema, string } from "yup"

import { EProjectFormPages } from "components/ProjectForm/config"
import { IPocDetailsForm, setPocDetailsForm, setProjectId } from "components/ProjectForm/ProjectFormSlice"
import { EAccountRole } from "enums/commonEnums"
import { ESubModuleIds } from "enums/ModuleEnum"
import { useLazyGetAccountDetailsQuery } from "pages/Accounts/AccountsServices"
import { DocHubUrlInfo, useSubmitFinalUploadsMutation, useUploadDocumentsMutation } from "pages/Common/fileUploadService"
import {
    IRaiseDemolitionPayload,
    useRaiseDemolitionMutation,
} from "pages/Operations/features/Demolition/DemolitionService"
import {
    IPocDetailsPageData,
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"
import { ProjectFormPageProps } from "../.."
import axios from "axios"

export enum EPocDetailsFields {
    ON_GROUND_POC_NAME = "on_ground_poc_name",
    ON_GROUND_POC_PHONE = "on_ground_poc_phone",
    ON_GROUND_POC_EMAIL = "on_ground_poc_email",
}

const initialValues: IPocDetailsForm = {
    [EPocDetailsFields.ON_GROUND_POC_NAME]: "",
    [EPocDetailsFields.ON_GROUND_POC_PHONE]: "",
    [EPocDetailsFields.ON_GROUND_POC_EMAIL]: "",
}

const validationSchema: ObjectSchema<IPocDetailsForm> = object({
    [EPocDetailsFields.ON_GROUND_POC_NAME]: string().trim().required("Name is required"),
    [EPocDetailsFields.ON_GROUND_POC_PHONE]: string()
        .trim()
        .required("Contact is required")
        .matches(/^[0-9]{10}$/, "Contact must be a valid 10-digit number"),
    [EPocDetailsFields.ON_GROUND_POC_EMAIL]: string()
        .trim()
        .required("Email is required")
        .email("Please enter a valid email"),
})

const usePocDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const {
        poc_details_form,
        account_n_sites_form,
        account_n_sites_form_values,
        project_id,
        deal_details_form,
        project_basic_details_form,
        payment_details_form,
        demolition_materials_form,
        demolition_pending_deal_docs,
    } = useSelector((state: TStore) => state.ProjectForm)
    const { user_id } = useSelector((store: any) => store.verifyLogin)
    const [getAccountDetails] = useLazyGetAccountDetailsQuery()
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()
    const [raiseDemolition, { isLoading: isRaisingDemolition }] = useRaiseDemolitionMutation()
    const [uploadDocuments] = useUploadDocumentsMutation()
    const [submitFinalUploads] = useSubmitFinalUploadsMutation()

    const [sameAsAccountContact, setSameAsAccountContact] = useState(false)
    const hasPrefilled = useRef(false)

    const formik = useFormik<IPocDetailsForm>({
        initialValues: poc_details_form ? { ...poc_details_form } : { ...initialValues },
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const fetchAndPrefillPocData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.ON_GROUND_POC,
            }).unwrap()

            const pageFormData = response?.pages?.[EProjectFormPages.ON_GROUND_POC]
            if (!pageFormData) return

            hasPrefilled.current = true
            const data = pageFormData.page_data || pageFormData

            if (data.on_ground_poc_name)
                formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_NAME, data.on_ground_poc_name)
            if (data.on_ground_poc_phone)
                formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_PHONE, data.on_ground_poc_phone)
            if (data.on_ground_poc_email)
                formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_EMAIL, data.on_ground_poc_email)
        } catch (error) {
            console.error("Error fetching POC prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, getProjectFormDetailsByStep, formik])

    useEffect(() => {
        fetchAndPrefillPocData()
    }, [fetchAndPrefillPocData])

    const hasError = (field: EPocDetailsFields) => Boolean(formik.submitCount > 0 && formik.errors[field])

    const handleSameAsAccountContactChange = async (checked: boolean) => {
        setSameAsAccountContact(checked)

        if (checked) {
            const buyerAccountData = account_n_sites_form_values?.accountRoles?.[EAccountRole.BUYER]
            const accountId = buyerAccountData?.account?.id
            const contactId = buyerAccountData?.contact?.id

            if (accountId && contactId) {
                try {
                    const accountDetails = await getAccountDetails(accountId as number).unwrap()
                    const contact = accountDetails.account_contacts?.find(c => c.id === contactId)

                    if (contact) {
                        formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_NAME, contact.name || "")
                        formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_PHONE, contact.phone || "")
                        formik.setFieldValue(EPocDetailsFields.ON_GROUND_POC_EMAIL, contact.email || "")
                    }
                } catch (error) {
                    console.error("Error fetching account details:", error)
                }
            }
        }
    }

    async function submitDemolitionRaise(values: IPocDetailsForm) {
        const basicDetails = project_basic_details_form as any
        const templateId = deal_details_form?.template_id?.id
        const dealType = deal_details_form?.deal_type?.id as "Outright" | "CPO" | undefined
        const reasonId = basicDetails?.demolition_reason?.id
        const sourceSiteId = account_n_sites_form?.source_site_id
        const destinationSiteId = account_n_sites_form?.destination_site_id
        const dealDoneById = basicDetails?.deal_done_by_id?.id

        if (
            !templateId ||
            !dealType ||
            !basicDetails?.project_name ||
            !dealDoneById ||
            !sourceSiteId ||
            !destinationSiteId ||
            !reasonId
        ) {
            return
        }

        const accounts = (account_n_sites_form?.account_roles || [])
            .filter(r => r.account_id)
            .map(r => ({
                account_id: r.account_id,
                account_type_id: r.account_type_id,
                billing_address_id: r.billing_address_id,
                contact_id: r.contact_id,
                contact_type_id: r.contact_type_id,
            }))

        const isPaymentTermsSpecified = Boolean(payment_details_form?.payment_terms)
        const totalAmount = isPaymentTermsSpecified ? Number(payment_details_form?.total_amount) || 0 : 0
        const paymentTermsObj = isPaymentTermsSpecified
            ? (payment_details_form?.payment_terms_obj || []).map((term: any) => {
                  const isCustomDate = term?.pmt_type?.id === 0
                  const isConditionalTimeFrame = term?.pmt_type?.id === 1
                  const usesAmount = term?.allocation_type?.id === "amount"

                  const termPayload: any = {
                      payment_term_id: term?.payment_term_id || null,
                      pmt_type: term?.pmt_type?.id ?? 0,
                      bank_guarantee: term?.bank_guarantee ? 1 : 0,
                      condition: term?.condition?.id ?? 0,
                      condition_text: term?.condition?.title || "",
                      ...(usesAmount
                          ? { pmt_amount: Number(term?.pmt_amount) || 0 }
                          : { pmt_percent: Number(term?.pmt_percent) || 0 }),
                      ...(isCustomDate ? { pmt_due_date: term?.pmt_due_date || "" } : {}),
                      ...(isConditionalTimeFrame
                          ? { time_qty: Number(term?.time_qty) || 0, time_uom: term?.time_uom?.id ?? 1 }
                          : {}),
                      ...(term?.condition?.id === 7
                          ? {
                                payment_evidence: {
                                    utr: term?.utr || "",
                                    original_amt: term?.original_amt || "",
                                    txn_due_date: term?.txn_due_date || "",
                                    paid_at: term?.paid_at || "",
                                    net_deduction: term?.net_deduction || "",
                                    remarks: term?.remarks || "",
                                    ...(term?.transaction_id ? { id: term.transaction_id } : {}),
                                },
                            }
                          : {}),
                  }

                  return termPayload
              })
            : []

        const payload: IRaiseDemolitionPayload = {
            template_id: Number(templateId),
            deal_type: dealType,
            project_name: basicDetails.project_name,
            deal_done_by_id: Number(dealDoneById),
            source_site_id: Number(sourceSiteId),
            destination_site_id: Number(destinationSiteId),
            reason_id: Number(reasonId),
            project_closure_date: basicDetails.project_closure_date || undefined,
            is_site_demolition_req: demolition_materials_form?.is_site_demolition_req || false,
            accounts: accounts.length ? accounts : undefined,
            program_id: basicDetails.program_id?.id ? Number(basicDetails.program_id.id) : undefined,
            category_id: basicDetails.category_id?.id ? Number(basicDetails.category_id.id) : undefined,
            total_amount: totalAmount,
            payment_terms: isPaymentTermsSpecified ? 1 : 0,
            payment_terms_obj: paymentTermsObj,
            on_ground_poc_name: values[EPocDetailsFields.ON_GROUND_POC_NAME] || undefined,
            on_ground_poc_phone: values[EPocDetailsFields.ON_GROUND_POC_PHONE] || undefined,
            on_ground_poc_email: values[EPocDetailsFields.ON_GROUND_POC_EMAIL] || undefined,
            plp_ids: demolition_materials_form?.plp_ids?.length ? demolition_materials_form.plp_ids : undefined,
        }

        try {
            const result = await raiseDemolition(payload).unwrap()
            const projectId = result.project_id
            dispatch(setProjectId(projectId))
            dispatch(setPocDetailsForm(values))

            await uploadDemolitionDealDocs(projectId)

            enqueueSnackbar(`Demolition ${result.display_id} created successfully`, { variant: "success" })
            props.handleNextStep(projectId)
        } catch (error: any) {
            console.error("Error raising demolition ticket:", error)
            enqueueSnackbar(error?.data?.message || error?.message || "Error creating demolition ticket", {
                variant: "error",
            })
        }
    }

    async function uploadDemolitionDealDocs(projectId: number) {
        if (!demolition_pending_deal_docs || demolition_pending_deal_docs.files.length === 0) return

        try {
            const response = await uploadDocuments({
                ticket_id: projectId,
                user_id,
                files: [
                    {
                        file_type: demolition_pending_deal_docs.file_type_id,
                        file_name: demolition_pending_deal_docs.files.map(f => f.name),
                    },
                ],
            }).unwrap()

            const urls = response.urls[0]?.urls || []
            if (urls.length > 0) {
                await Promise.all(
                    urls.map(async (urlInfo: DocHubUrlInfo, index: number) => {
                        await axios.put(urlInfo.presigned_url, demolition_pending_deal_docs.files[index], {
                            headers: { "Content-Type": demolition_pending_deal_docs.files[index]?.type },
                        })
                    })
                )

                await submitFinalUploads({
                    ticket_id: projectId,
                    file_infos: urls.map((urlInfo: DocHubUrlInfo) => ({
                        file_url: urlInfo.file_url,
                        doc_id: urlInfo.doc_id,
                        report_id: urlInfo.report_id,
                    })),
                }).unwrap()
            }
        } catch (error) {
            console.error("Error uploading deal docs:", error)
        }
    }

    async function handleSubmit(values: IPocDetailsForm) {
        if (props.subModuleId === ESubModuleIds.DEMOLITION && !props.isEditMode) {
            await submitDemolitionRaise(values)
            return
        }

        if (!project_id) {
            enqueueSnackbar("Project ID is required", { variant: "error" })
            return
        }

        const payload: IPocDetailsPageData = {
            page_title: EProjectFormPages.ON_GROUND_POC,
            project_id,
            [EPocDetailsFields.ON_GROUND_POC_NAME]: values[EPocDetailsFields.ON_GROUND_POC_NAME],
            [EPocDetailsFields.ON_GROUND_POC_PHONE]: values[EPocDetailsFields.ON_GROUND_POC_PHONE],
            [EPocDetailsFields.ON_GROUND_POC_EMAIL]: values[EPocDetailsFields.ON_GROUND_POC_EMAIL],
        }

        try {
            await updateTicketPageData(payload).unwrap()

            dispatch(setPocDetailsForm(values))

            enqueueSnackbar(`Project created successfully `, { variant: "success" })
            props.handleNextStep()
        } catch (error) {
            console.error("Error updating POC details:", error)
            enqueueSnackbar("Error saving POC details", { variant: "error" })
        }
    }

    return {
        states: {
            formik,
            sameAsAccountContact,
            isUpdatingPageData: isUpdatingPageData || isRaisingDemolition,
        },
        functions: {
            hasError,
            handleSameAsAccountContactChange,
        },
    }
}

export default usePocDetails
